/**
 * @module app.core
 */

import { autoinject, observable } from 'aurelia-framework';
import { AssessmentContext } from 'app/core/lib/assessment-context';
import { IQuestionComponent, IQuestionConfig } from 'app/core/components/assessments/interfaces';
import { QuestionBase } from 'app/core/components/assessments/questions/question-base';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { FormatProvider } from 'app/shared/lib/formatting/format-provider';
import { DateValue } from 'app/shared/lib/date-time';
import { I18N } from 'aurelia-i18n';
import * as Core from 'app/core/models/essette-api';


interface IMonth {
    id: number;
    value: string;
    description: string
}

@autoinject()
export class QuestionMonthYearCustomElement extends QuestionBase implements IQuestionComponent {

    ////****************** Fields and Properties ************************************////

    protected month: IMonth | null = null;
    protected year: number | null = null;
    protected thisYear: number | null = null;
    protected YEAR_LENGTH = 4;

    protected months: IMonth[] = [
        { id: 1, value: '01', description: 'core:calendar.month.january.$' },
        { id: 2, value: '02', description: 'core:calendar.month.february.$' },
        { id: 3, value: '03', description: 'core:calendar.month.march.$' },
        { id: 4, value: '04', description: 'core:calendar.month.april.$' },
        { id: 5, value: '05', description: 'core:calendar.month.may.$' },
        { id: 6, value: '06', description: 'core:calendar.month.june.$' },
        { id: 7, value: '07', description: 'core:calendar.month.july.$' },
        { id: 8, value: '08', description: 'core:calendar.month.august.$' },
        { id: 9, value: '09', description: 'core:calendar.month.september.$' },
        { id: 10, value: '10', description: 'core:calendar.month.october.$' },
        { id: 11, value: '11', description: 'core:calendar.month.november.$' },
        { id: 12, value: '12', description: 'core:calendar.month.december.$' }
    ]


    ////****************** Element References ***************************************////



    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        protected readonly context: AssessmentContext,
        private readonly env: EnvironmentService,
        private readonly formatProvider: FormatProvider,
        private readonly i18n: I18N,
        private readonly logger: AppLogger) {

        super(context);
        this.logger.source = this;

        this.thisYear = new DateValue(this.env.today).toParts().year;

        if(!context || !context.currentSession) {
            throw new Error("No Assessment context is available");
        }

        if(!this.context.questionComponents.includes(this)) {
            this.context.questionComponents.push(this);
        }
    }

    protected async activate(config: IQuestionConfig): Promise<void> {
        this.question = config.question;
        this.required = !!this.question.flag?.isRequired;
        this.showHistory = config.showHistory;
        this.readonly = config.readonly;
        this.isValid = !this.required;
        this.labelId = config.labelId;

        // Translate Months
        this.months.forEach(m => {
            m.description = this.i18n.tr(m.description);
        });
    }

    protected attached(): void {
        this.loadPreviousAnswer();
    }


    ////****************** Events ***************************************************////


    ////****************** Functions ************************************************////

    private async loadPreviousAnswer(): Promise<void> {
        const questionId = this.question && this.question.id;
        if(!questionId || !this.context.attachment.currentAnswers) { return; }

        const description = this.question && this.question.description || '';

        try {
            const answer = this.context.attachment.currentAnswers.find(a => {
                if(!a.question) { return false; }

                if(!this.question.sharedQuestion) {
                    return a.question.id === this.question.id;
                } else if(!!this.question.sharedQuestion && !!a.sharedQuestion) {
                    return this.question.sharedQuestion.id === a.sharedQuestion.id;
                }

                return false;

            });

            if(answer) {
                this.parseAnswerText(answer.value || '');
            }
        }
        catch(error) {
            this.logger.error(`Find existing answer error: ${error}`);
            this.logger.error(`Unable to find existing answer(s) for question ${questionId} (${description})`);
            throw error;
        }

    }

    private parseAnswerText(answerText: string): void {
        const pieces = answerText.split('-');
        if(pieces.length !== 2) {
            this.logger.warn(`Answer contains invalid value. Expected format: yyyy-MM. Got '${answerText}'`);
            return;
        }

        try {
            this.year = this.formatProvider.parseNumber(pieces[0]);
            const month = this.formatProvider.parseNumber(pieces[1]);
            this.month = this.months.find(m => { return m.id === month; }) || null;
        }
        catch(error) {
            throw new Error('Unable to parse values');
        }
    }


    ////****************** IQuestionComponent ****************************************************////
    public saveAnswer = null;  // Not Implemented

    public getAnswers(): Core.AssessmentAnswer[] {
        if(this.month && this.year && this.year.toString().length === this.YEAR_LENGTH) {
            const answer: Core.AssessmentAnswer = {
                question: { id: this.question && this.question.id || null },
                text: `${this.year}-${this.month.value}`
            };

            return [answer];
        }

        return [];
    }

}
